import { InternalRefetchQueriesInclude } from '@apollo/client';
import { DAY_INDEX_MAP } from '@modules/subscribe/components/SubscribeDrawerV2/OutboundFrequencyCard/constants';
import {
  useCreateSubscription,
  useUpdateSubscription,
} from '@modules/subscribe/hooks';
import dayjs from 'dayjs';
import {
  ArtefactType,
  CronInput,
  DayName,
  DayOfMonth,
  GetDashboardByTokenDocument,
  SlackChannelInput,
  SubjectType,
  useCreateScheduleMutation,
  useUpdateScheduleMutation,
} from 'generated/graphql';
import { ReportingType } from 'v2/core/entities/report';
import { UUID } from 'v2/core/entities/uuid';
import { ReportSchedule } from 'v2/core/repository/edge-server/analysis/types';
import { useGetUser } from 'v2/core/services/user';
import { Cron } from '../types';

interface SaveReportSubscriptionOptions {
  slackChannels: SlackChannelInput[];
  emails: string[];
  cron: Cron;
}

interface ReportArtefact {
  id: string;
  token: string;
  type: ArtefactType;
}

interface UseSubscribeToDashboardOptions {
  reportSchedule: ReportSchedule | undefined;
  reportArtefact: ReportArtefact;
  skipPDFInReport?: boolean;
  shouldRefetchQueries?: boolean;
  refetchQueries?: InternalRefetchQueriesInclude;
  subjectType?: SubjectType;
}

export const useSubscribeToDashboard = ({
  reportSchedule,
  reportArtefact,
  skipPDFInReport,
  shouldRefetchQueries = false,
  refetchQueries,
  subjectType,
}: UseSubscribeToDashboardOptions) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const { createSubscription, isLoading: isCreatingSubscription } =
    useCreateSubscription();

  const { updateSubscription, isLoading: isUpdatingSubscription } =
    useUpdateSubscription({
      integrations: reportSchedule?.integrations ?? [],
      outbounds: reportSchedule?.outbounds ?? [],
    });

  const refetchDashboardQuery = {
    query: GetDashboardByTokenDocument,
    variables: {
      userID,
      tenantID: tenantID,
      token: reportArtefact.token,
      includeScheduleOutbounds: true,
    },
  };

  const getRefetchQueries = () => {
    if (refetchQueries) {
      return refetchQueries;
    }

    if (shouldRefetchQueries) {
      return [refetchDashboardQuery];
    }

    return undefined;
  };

  const [
    createScheduleMutation,
    { data: createScheduleData, loading: isCreatingSchedule },
  ] = useCreateScheduleMutation({
    refetchQueries: getRefetchQueries(),
  });

  const [
    updateScheduleMutation,
    { data: updateScheduledata, loading: isUpdatingSchedule },
  ] = useUpdateScheduleMutation({
    refetchQueries: getRefetchQueries(),
  });

  const subscribeToDashboard = async ({
    slackChannels,
    emails,
    cron,
  }: SaveReportSubscriptionOptions) => {
    const cronValue = cron.valueV2;
    // Translate cron values to UTC
    // For hourOfDay and minuteOfHour
    const userSelectedTime = dayjs()
      .hour(cronValue?.hourOfDay ?? 0)
      .minute(0);

    const UTCHour = userSelectedTime.utc().hour();
    const UTCMinute = userSelectedTime.utc().minute();

    // For dayOfWeek
    // It can happen that user selected day and time falls
    // to a different day in UTC
    const UTCDayOfTheWeek = cronValue?.dayOfWeek?.map((day) => {
      const newUTCHourDate = dayjs()
        .startOf('month') // start of the month
        .set(
          'day',
          DAY_INDEX_MAP[day ?? cronValue?.dayOfWeek?.[0] ?? DayName.Invalid],
        ) // go to the required date because we know which day of the week it is
        .set('hour', cronValue?.hourOfDay ?? 0) // set the hour of the day
        .utc();

      return DayName[newUTCHourDate.format('ddd') as keyof typeof DayName];
    });

    const UTCDayOfMonth = ((): DayOfMonth | undefined => {
      if (!cronValue?.dayOfMonth?.day) {
        return cronValue?.dayOfMonth;
      }

      const dayOfMonth = cronValue?.dayOfMonth?.day;
      const dayOfMonthUTC = dayjs()
        .set('month', 0) // January for full month of 31 days
        .set('date', dayOfMonth)
        .set('hour', cronValue?.hourOfDay ?? 0)
        .set('minute', cronValue?.minuteOfHour ?? 0)
        .utc()
        .date();

      // When local day converted to UTC is 31, we need to set the last day of the month as the day of the month i.e. "last: true"
      // This is because the last day of the month can be 30 or 31 depending on the month
      // Backend can't automatically figure out the last day of the month because of limitation in the cron library
      if (dayOfMonthUTC === 31) {
        return { last: true };
      }

      return { day: dayOfMonthUTC };
    })();

    const cronData: CronInput = {
      realtime: cron.realtime ?? false,
      valueV2: {
        dayOfMonth: UTCDayOfMonth,
        dayOfWeek: UTCDayOfTheWeek,
        hourOfDay: UTCHour,
        minuteOfHour: UTCMinute,
      },
    };

    const scheduleMetadata = {
      defaultReportMetadata: {
        skipSnapshot: skipPDFInReport,
      },
    };

    if (!reportSchedule) {
      await createSubscription(
        async (outboundIDs: UUID[]) => {
          await createScheduleMutation({
            variables: {
              tenantID,
              subjectID: reportArtefact.id,
              metadata: scheduleMetadata,
              outbounds: outboundIDs,
              cron: cronData,
              subjectType: subjectType ?? SubjectType.Dashboard,
              reportType: ReportingType.DefaultReport,
            },
          });
        },
        slackChannels,
        emails,
      );
      return;
    }

    await updateSubscription(
      async (outboundIDs: UUID[]) => {
        await updateScheduleMutation({
          variables: {
            tenantID,
            cron: cronData,
            outbounds: outboundIDs,
            id: reportSchedule.id,
            metadata: scheduleMetadata,
          },
        });
      },
      slackChannels,
      emails,
      true,
    );
  };

  const isLoading =
    isCreatingSubscription ||
    isUpdatingSubscription ||
    isCreatingSchedule ||
    isUpdatingSchedule;

  return {
    subscribeToDashboard,
    isLoading,
    createdScheduleID: createScheduleData?.createSchedule?.id,
    isScheduleUpdated: updateScheduledata?.updateSchedule?.updated,
  };
};
